import api from './api';
// import { handleApiError } from '../utils/errorHandler'; // REMOVE - Function doesn't exist

// Flag to toggle between direct API calls and Firebase Functions proxy
// This should match the setting in authService.js
// const USE_FIREBASE_PROXY = true; // Keep commented out or remove

const customerService = {
  // Get all customers
  getCustomers: async () => {
    console.log('👥 GET CUSTOMERS: Fetching all customers');
    try {
      const url = '/customers';
      const response = await api.get(url);
      console.log('👥 Customers fetched successfully:', { count: response.data.count, headers: response.headers });
      return response.data.data || response.data; 
    } catch (error) {
      // Remove handleApiError call, use console.error directly
      console.error('Error fetching customers:', error);
      throw error;
    }
  },
  
  // Get customer by ID - Renamed for consistency
  getCustomerById: async (id) => {
    console.log(`👤 GET CUSTOMER BY ID: Fetching customer ${id}`);
    if (!id) throw new Error('Customer ID is required');
    try {
      const url = `/customers/${id}`;
      const response = await api.get(url); 
      console.log(`👤 Customer ${id} fetched successfully:`, { data: response.data, headers: response.headers });
      return response.data.data || response.data; 
    } catch (error) {
      // Remove handleApiError call, use console.error directly
      console.error(`Error fetching customer ${id}:`, error);
      throw error;
    }
  },
  
  // Get recent contacts (last 30 days)
  getRecentContacts: async () => {
    console.log('🕒 GET RECENT CONTACTS: Fetching recent contacts');
    try {
      // Assuming endpoint exists, adjust if needed
      const response = await api.get('/customers?recent=true');
      console.log('🕒 Recent contacts fetched:', { count: response.data.count });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching recent contacts:', error);
      throw error;
    }
  },
  
  // Send customer to CRM
  sendToCrm: async (customerId) => {
    console.log(`🔗 SEND TO CRM: Sending customer ${customerId}`);
    if (!customerId) throw new Error('Customer ID required for CRM sync');
    try {
      // Assuming endpoint exists, interceptor handles CSRF for POST
      const response = await api.post('/zapier/send-customer', { customerId });
      console.log(`🔗 CRM Sync initiated for ${customerId}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error sending customer ${customerId} to CRM:`, error);
      throw error;
    }
  },
  
  // Update customer 
  updateCustomer: async (id, customerData) => {
    console.log(`👤 UPDATE CUSTOMER: Attempting to update customer ${id}`, customerData);
    if (!id) throw new Error('Customer ID is required');
    try {
      const url = `/customers/${id}`;
      const response = await api.put(url, customerData); 
      console.log(`👤 Customer ${id} updated successfully:`, { data: response.data, headers: response.headers });
      return response.data.data || response.data;
    } catch (error) {
      // Remove handleApiError call, use console.error directly
      console.error(`Error updating customer ${id}:`, error);
      throw error;
    }
  },
  
  // Assign customer to user
  assignCustomer: async (customerId, userId) => {
    console.log(`👤 ASSIGN CUSTOMER: Assigning customer ${customerId} to user ${userId}`);
    if (!customerId || !userId) throw new Error('Customer ID and User ID are required');
    try {
      // No explicit CSRF needed - interceptor handles it for PUT
      const response = await api.put(`/customers/${customerId}/assign`, { userId });
      console.log(`👤 Customer ${customerId} assigned to ${userId}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error assigning customer ${customerId}:`, error);
      throw error;
    }
  },
  
  // Bulk assign customers to user
  bulkAssignCustomers: async (customerIds, userId) => {
    console.log(`👤 BULK ASSIGN: Assigning ${customerIds.length} customers to user ${userId}`);
    if (!customerIds || customerIds.length === 0 || !userId) {
       throw new Error('Customer IDs array and User ID are required');
    }
    try {
      // No explicit CSRF needed - interceptor handles it for PUT
      const response = await api.put('/customers/bulk-assign', { customerIds, userId });
      console.log(`👤 Bulk assignment to ${userId} complete:`, response.data);
      return response.data;
    } catch (error) {
      console.error('Error bulk assigning customers:', error);
      throw error;
    }
  },
  
  // Create a new customer
  createCustomer: async (customerData) => {
    console.log('👤 CREATE CUSTOMER: Attempting to create new customer', customerData);
    try {
      const url = '/customers';
      const response = await api.post(url, customerData); 
      console.log('👤 Customer created successfully:', { data: response.data, headers: response.headers });
      return response.data.data || response.data;
    } catch (error) {
      // Remove handleApiError call, use console.error directly
      console.error('Error creating customer:', error);
      throw error;
    }
  },
  
  // Delete customer
  deleteCustomer: async (id) => {
    console.log(`👤 DELETE CUSTOMER: Attempting to delete customer ${id}`);
    if (!id) throw new Error('Customer ID is required');
    try {
      const url = `/customers/${id}`;
      const response = await api.delete(url); 
      console.log(`👤 Customer ${id} deleted successfully:`, { status: response.status, headers: response.headers });
      return response.data || { success: true, message: 'Customer deleted' }; 
    } catch (error) {
      // Remove handleApiError call, use console.error directly
      console.error(`Error deleting customer ${id}:`, error);
      throw error;
    }
  },

  // Persist the "handled" flag on an emergency contact-history entry.
  // There is no dedicated backend endpoint for contact history yet, so this
  // works through the existing PUT /customers/:id by rewriting the
  // contactHistory array, then verifies the server actually persisted the
  // change (some backends whitelist updatable fields and would silently drop
  // it — in that case we throw so the UI can tell the user).
  markEmergencyHandled: async (customerId, contactDate) => {
    if (!customerId || !contactDate) {
      throw new Error('Customer ID and contact date are required');
    }

    const customer = await customerService.getCustomerById(customerId);
    const matches = (entry) =>
      entry.emergency && String(entry.date) === String(contactDate);

    if (!(customer.contactHistory || []).some(matches)) {
      throw new Error('Emergency contact entry not found on customer');
    }

    const contactHistory = customer.contactHistory.map((entry) =>
      matches(entry) ? { ...entry, handled: true } : entry
    );

    const updated = await customerService.updateCustomer(customerId, { contactHistory });

    const persisted = (updated.contactHistory || []).some(
      (entry) => matches(entry) && entry.handled === true
    );
    if (!persisted) {
      throw new Error(
        'The server did not save the handled status — the customer update endpoint may not accept contact history changes yet.'
      );
    }

    return updated;
  },

  // Get all customer assessments - Uses API client
  getCustomerAssessments: async () => {
    console.log('📊 GET ASSESSMENTS: Fetching all customer assessments');
    try {
      const url = '/customers/assessments';
      const response = await api.get(url); 
      console.log('📊 Assessments fetched successfully:', { count: response.data.count, headers: response.headers });
      return response.data.data || response.data;
    } catch (error) {
      // Remove handleApiError call, use console.error directly
      console.error('Error fetching customer assessments:', error);
      throw error;
    }
  }
};

export default customerService;